import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, completePasswordReset } from "../api/client";
import { RequiredMark } from "../ui/required-mark";

/**
 * Spend a password-reset link — **the page the emails have been pointing at
 * since 2026-08-31, which did not exist until now.**
 *
 * `passwordResetUrl` builds `{clinic origin}/reset/{token}`, the mail template
 * puts it behind a button, and the route table had nothing for it. React Router
 * matched no route, there was no catch-all either, and the reader got a
 * completely blank page. That is the whole bug: the feature was built, tested
 * and shipped on the server, and the last three hundred metres were missing.
 *
 * ── It does not sign anybody in ─────────────────────────────────────────────
 *
 * Unlike accepting an invite, which lands signed in because the invite *was*
 * the credential. Here the credential is an emailed link, and the point of a
 * reset is that somebody may have lost control of the account — so the new
 * password is set and then typed once, deliberately, at the sign-in screen.
 * That also means every session the old password held has already been revoked
 * by the time they arrive.
 *
 * ── The token is never inspected here ───────────────────────────────────────
 *
 * No decoding, no expiry check, no "this link looks old" message. Expired,
 * forged, already-spent and belonging-to-another-clinic are one sentence from
 * the server on purpose, and re-deriving any of it in the browser would leak
 * exactly what that sentence exists to withhold.
 */
export function ResetPasswordPage(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // A typing aid, not a rule — the server never hears about it.
    if (password !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }

    setSubmitting(true);
    try {
      await completePasswordReset(token ?? "", password);
      await navigate("/login", { replace: true, state: { passwordWasReset: true } });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      setSubmitting(false);
    }
  }

  return (
    <main className="sheet-wide">
      <h1>Choose a new password</h1>
      <p>
        Once you save it, every device signed in with the old password is signed out. You will sign
        in again with the new one.
      </p>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label htmlFor="reset-password">
          New password
          <RequiredMark />
        </label>
        <input
          id="reset-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          data-testid="reset-password"
        />

        <label htmlFor="reset-confirm">
          New password again
          <RequiredMark />
        </label>
        <input
          id="reset-confirm"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          data-testid="reset-confirm"
        />

        {error !== null && (
          <p role="alert" className="error" data-testid="reset-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} data-testid="reset-submit">
          {submitting ? "Saving…" : "Save and sign in"}
        </button>
      </form>

      <p>
        <Link to="/login">Back to sign in</Link>
      </p>
    </main>
  );
}
